import React, { useRef, useEffect, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import WaveformPlaceholder from './WaveformPlaceholder'

/**
 * Waveform mit wavesurfer.js inkl. Play/Pause und Zeit-Anzeige.
 * Container bleibt immer gemountet (kein Ref-Lifecycle-Deadlock nach Error).
 */
export default function Waveform({ audioUrl, height = 48, interact = true, animate = false, className = '', showControls = true, controlsPosition = 'bottom', waveColor = '#00d4aa', progressColor = '#5eead4' }) {
  const containerRef = useRef(null)
  const wsRef = useRef(null)
  const [status, setStatus] = useState(audioUrl ? 'loading' : 'idle')
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    if (!audioUrl) {
      setStatus('idle')
      return
    }

    let cancelled = false
    let ws = null

    const start = () => {
      if (cancelled || !containerRef.current) return
      setStatus('loading')
      setIsPlaying(false)
      setCurrentTime(0)
      setDuration(0)

      ws = WaveSurfer.create({
        container: containerRef.current,
        height,
        waveColor,
        progressColor,
        cursorColor: '#00d4aa',
        cursorWidth: 1,
        barWidth: 2,
        barGap: 1,
        barRadius: 1,
        normalize: true,
        fillParent: true,
        interact,
        backend: 'MediaElement',
        // Niedrig = Übersicht ohne teures Zoom-Decode (schnelleres Peak-Rendering)
        minPxPerSec: 1,
      })
      wsRef.current = ws

      const onReady = () => {
        if (cancelled) return
        setStatus('ready')
        setDuration(ws.getDuration() || 0)
      }
      const onError = () => {
        if (!cancelled) setStatus('error')
      }
      const onPlay = () => setIsPlaying(true)
      const onPause = () => setIsPlaying(false)
      const onTimeupdate = (t) => setCurrentTime(t)

      ws.on('ready', onReady)
      ws.on('error', onError)
      ws.on('play', onPlay)
      ws.on('pause', onPause)
      ws.on('timeupdate', onTimeupdate)
      ws.load(audioUrl).catch((err) => {
        if (!cancelled && err?.name !== 'AbortError') setStatus('error')
      })
    }

    // Ref erst nach Paint verfügbar (wichtig nach Error→Retry)
    const raf = requestAnimationFrame(start)

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      if (ws) {
        try {
          ws.destroy()
        } catch {
          /* ignore */
        }
      }
      wsRef.current = null
    }
  }, [audioUrl, height, interact, waveColor, progressColor])

  const togglePlayPause = () => {
    const ws = wsRef.current
    if (ws) ws.playPause()
  }

  const formatTime = (s) => {
    if (s == null || !Number.isFinite(s)) return '0:00'
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${String(sec).padStart(2, '0')}`
  }

  if (!audioUrl) {
    return <WaveformPlaceholder className={className} animate={animate} />
  }

  const controlsBlock = status === 'ready' && showControls && (
    <div className="flex shrink-0 flex-col items-center justify-center gap-1">
      <button
        type="button"
        onClick={togglePlayPause}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#00d4aa]/15 text-[#00d4aa] transition hover:bg-[#00d4aa]/25"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <span className="text-lg leading-none">⏸</span>
        ) : (
          <span className="ml-0.5 text-lg leading-none">▶</span>
        )}
      </button>
      <span className="text-xs text-white/60 tabular-nums">
        {formatTime(currentTime)} / {formatTime(duration)}
      </span>
    </div>
  )

  const waveformBlock = (
    <div className="relative min-w-0 flex-1" style={{ minHeight: height }}>
      <div ref={containerRef} className="w-full" style={{ height }} />
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg overflow-hidden">
          <WaveformPlaceholder className="w-full" height={height} animate />
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-lg bg-black/40">
          <WaveformPlaceholder height={Math.min(height, 48)} animate={false} />
          <span className="text-xs text-white/50">Waveform konnte nicht geladen werden</span>
        </div>
      )}
    </div>
  )

  if (controlsPosition === 'left') {
    return (
      <div className={`flex items-center gap-4 ${className}`}>
        {controlsBlock}
        {waveformBlock}
      </div>
    )
  }

  return (
    <div className={`relative ${className}`} style={{ minHeight: height }}>
      {waveformBlock}
      {status === 'ready' && showControls && (
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={togglePlayPause}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00d4aa]/15 text-[#00d4aa] transition hover:bg-[#00d4aa]/25"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <span className="text-lg leading-none">⏸</span>
            ) : (
              <span className="ml-0.5 text-lg leading-none">▶</span>
            )}
          </button>
          <span className="text-xs text-white/60 tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>
      )}
    </div>
  )
}
